import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Checkbox, DatePicker, Empty, Input, Popconfirm, Segmented, Select, Space, Spin, Table, Tag, Tooltip, Typography, message } from 'antd'
import { DeleteOutlined, PlusOutlined, ReloadOutlined, UndoOutlined, UserAddOutlined } from '@ant-design/icons'
import dayjs, { type Dayjs } from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { tasksApi, type TaskItem } from '../services/tasksApi'
import {
    CLOSED, ChatIcon, DATE_FORMAT, DoneIcon, EditIcon, PRIORITY_LABELS, STATUS_LABELS, extractError, useTaskEditor,
} from '../components/Tasks/taskShared'

const { Text, Title } = Typography
const { RangePicker } = DatePicker

type Tab = 'open' | 'closed'
// Разбивка активных задач по сроку. Корзины не пересекаются: задача на сегодня,
// время которой уже прошло, - просроченная, а не «на сегодня».
type Due = 'all' | 'overdue' | 'today' | 'tomorrow'


// К какой дате применяется выбранный период.
type RangeBasis = 'deadline' | 'created' | 'closed'
const BASIS_LABELS: Record<RangeBasis, string> = {
    deadline: 'Период: по сроку',
    created: 'Период: по постановке',
    closed: 'Период: по закрытию',
}

const dueOf = (task: TaskItem, now: Dayjs): Exclude<Due, 'all'> | null => {
    if (!task.deadline) return null
    const deadline = dayjs(task.deadline)
    if (deadline.isBefore(now)) return 'overdue'
    if (!deadline.isAfter(now.endOf('day'))) return 'today'
    const tomorrow = now.add(1, 'day')
    if (!deadline.isBefore(tomorrow.startOf('day')) && !deadline.isAfter(tomorrow.endOf('day'))) return 'tomorrow'
    return null
}

export default function LeadTasksBoard() {
    const navigate = useNavigate()

    const [tasks, setTasks] = useState<TaskItem[]>([])
    const [loading, setLoading] = useState(false)
    const [tab, setTab] = useState<Tab>('open')
    const [due, setDue] = useState<Due>('all')
    const [assignee, setAssignee] = useState<number | undefined>(undefined)
    // Только задачи моих команд.
    const [teamOnly, setTeamOnly] = useState(false)
    const { user } = useAuthStore()

    // Период и то, к какой дате он применяется. Один набор на обе вкладки:
    // отбирать можно по сроку, по времени постановки и - у закрытых - по дате
    // закрытия.
    const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null)
    const [basis, setBasis] = useState<RangeBasis>('deadline')
    const [priorityFilter, setPriorityFilter] = useState<string | undefined>(undefined)
    const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined)
    const [search, setSearch] = useState('')

    // Текущее время освежается раз в полминуты: задача сама переезжает в
    // «Просроченные», как только её время прошло, без перезагрузки страницы.
    const [now, setNow] = useState(() => dayjs())
    useEffect(() => {
        const timer = window.setInterval(() => setNow(dayjs()), 30000)
        return () => window.clearInterval(timer)
    }, [])

    const load = useCallback(async () => {
        setLoading(true)
        try {
            // Границы суток считает браузер - по часовому поясу человека.
            const from = range ? range[0].startOf('day').toISOString() : null
            const to = range ? range[1].endOf('day').toISOString() : null
            const period = !from || !to ? {}
                : basis === 'closed' ? { closed_from: from, closed_to: to }
                : basis === 'created' ? { created_from: from, created_to: to }
                : { due_from: from, due_to: to }
            const { data } = await tasksApi.getTasks({
                state: tab,
                ...(assignee ? { assigned_to: assignee } : {}),
                ...(teamOnly ? { team_only: true } : {}),
                ...period,
            })
            setTasks(data)
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setLoading(false)
        }
    }, [tab, assignee, teamOnly, range, basis])

    useEffect(() => {
        load()
    }, [load])

    // Окно задачи, закрытие с комментарием и действия - общие с карточкой лида.
    const { isManager, assignableOperators, openCreate, openEdit, requestClose, setStatus, remove, take, modals } = useTaskEditor({ onChanged: load })

    // Поиск работает на обеих вкладках и применяется первым: и разбивка по
    // сроку, и счётчики считаются уже по найденному, иначе в «Просроченных»
    // стояло бы одно число, а в списке - другое.
    const searchedTasks = useMemo(() => {
        const query = search.trim().toLowerCase()
        if (!query) return tasks
        return tasks.filter((task) => {
            const haystack = [task.title, task.description, task.lead_title, task.lead_crm_ww_id, task.assigned_to_name, task.visibility_profile_name]
                .filter(Boolean).join(' ').toLowerCase()
            return haystack.includes(query)
        })
    }, [tasks, search])

    const dueCounts = useMemo(() => {
        const counts = { overdue: 0, today: 0, tomorrow: 0 }
        for (const task of searchedTasks) {
            const bucket = dueOf(task, now)
            if (bucket) counts[bucket] += 1
        }
        return counts
    }, [searchedTasks, now])

    const visibleTasks = useMemo(() => {
        if (tab === 'open') {
            if (due === 'all') return searchedTasks
            return searchedTasks.filter((task) => dueOf(task, now) === due)
        }
        return searchedTasks.filter((task) => {
            if (priorityFilter && task.priority !== priorityFilter) return false
            if (statusFilter && task.status !== statusFilter) return false
            return true
        })
    }, [searchedTasks, tab, due, now, priorityFilter, statusFilter])

    const columns = [
        {
            title: 'Задача',
            dataIndex: 'title',
            key: 'title',
            render: (_: unknown, task: TaskItem) => (
                <Space direction="vertical" size={2} style={{ minWidth: 0 }}>
                    <Space size={6} wrap>
                        <Text strong={!CLOSED.includes(task.status)}>{task.title}</Text>
                        {/* Задача связана с их CRM - по номеру её там и искать. */}
                        {task.crm_task_id && <Tag color="purple">CRM #{task.crm_task_id}</Tag>}
                    </Space>
                    {task.description && (
                        <Text type="secondary" style={{ fontSize: 12 }}>{task.description}</Text>
                    )}
                    {task.close_comment && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Закрыта с комментарием: {task.close_comment}
                        </Text>
                    )}
                </Space>
            ),
        },
        {
            title: 'Лид',
            key: 'lead',
            width: 200,
            render: (_: unknown, task: TaskItem) => (task.lead_id ? (
                <Space direction="vertical" size={2}>
                    <Text>{task.lead_title || `Лид #${task.lead_id}`}</Text>
                    <Space size={6} wrap>
                        {task.lead_geo && <Tag>{task.lead_geo}</Tag>}
                        {task.lead_crm_ww_id && <Tag color="geekblue">CRM {task.lead_crm_ww_id}</Tag>}
                    </Space>
                    {task.lead_profile_name && (
                        <Text type="secondary" style={{ fontSize: 12 }}>Профиль: {task.lead_profile_name}</Text>
                    )}
                </Space>
            ) : <Text type="secondary">—</Text>),
        },
        {
            title: 'Срок',
            dataIndex: 'deadline',
            key: 'deadline',
            width: 180,
            render: (value: string | null, task: TaskItem) => {
                if (!value) return <Text type="secondary">без срока</Text>
                // Задача ставится на время: как только оно прошло - просрочена.
                const overdue = !CLOSED.includes(task.status) && dayjs(value).isBefore(now)
                return (
                    <Space direction="vertical" size={0}>
                        <Text type={overdue ? 'danger' : undefined}>{dayjs(value).format(DATE_FORMAT)}</Text>
                        {overdue && <Text type="danger" style={{ fontSize: 12 }}>просрочена</Text>}
                    </Space>
                )
            },
        },
        {
            title: 'Поставлена',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 170,
            render: (value: string) => <Text type="secondary">{dayjs(value).format(DATE_FORMAT)}</Text>,
        },
        ...(tab === 'closed' ? [{
            title: 'Закрыта',
            dataIndex: 'completed_at',
            key: 'completed_at',
            width: 180,
            render: (value: string | null, task: TaskItem) => {
                if (!value) return <Text type="secondary">—</Text>
                const late = task.deadline && dayjs(value).isAfter(dayjs(task.deadline))
                return (
                    <Space direction="vertical" size={0}>
                        <Text>{dayjs(value).format(DATE_FORMAT)}</Text>
                        {late && <Text type="warning" style={{ fontSize: 12 }}>после срока</Text>}
                    </Space>
                )
            },
        }] : []),
        {
            title: 'Исполнитель',
            dataIndex: 'assigned_to_name',
            key: 'assigned_to_name',
            width: 180,
            render: (value: string | null, task: TaskItem) => (
                <Space direction="vertical" size={2}>
                    {task.visibility_profile_id && <Tag color="geekblue">РП «{task.visibility_profile_name}»</Tag>}
                    {value
                        ? <Text>{value}</Text>
                        : <Text type="secondary">{task.visibility_profile_id ? 'возьмёт любой из РП' : 'не назначен'}</Text>}
                </Space>
            ),
        },
        {
            title: 'Приоритет',
            dataIndex: 'priority',
            key: 'priority',
            width: 120,
            render: (value: string) => {
                const item = PRIORITY_LABELS[value] || PRIORITY_LABELS.medium
                return <Tag color={item.color}>{item.label}</Tag>
            },
        },
        {
            title: 'Статус',
            dataIndex: 'status',
            key: 'status',
            width: 130,
            render: (value: string) => {
                const item = STATUS_LABELS[value] || STATUS_LABELS.pending
                return <Tag color={item.color}>{item.label}</Tag>
            },
        },
        {
            title: '',
            key: 'actions',
            width: 190,
            render: (_: unknown, task: TaskItem) => (
                <Space size={4}>
                    {task.lead_id && (
                        <Tooltip title="Открыть чат лида">
                            <Button
                                type="text"
                                className="task-icon-btn"
                                icon={<ChatIcon />}
                                onClick={() => navigate(`/chats?leadId=${task.lead_id}`)}
                            />
                        </Tooltip>
                    )}
                    {task.visibility_profile_id && task.assigned_to_id !== user?.id && !CLOSED.includes(task.status) && (
                        <Tooltip title="Взять себе">
                            <Button type="text" icon={<UserAddOutlined />} onClick={() => take(task)} />
                        </Tooltip>
                    )}
                    {!CLOSED.includes(task.status) ? (
                        <Tooltip title="Отметить выполненной">
                            <Button
                                type="text"
                                className="task-icon-btn"
                                icon={<DoneIcon />}
                                onClick={() => requestClose(task)}
                            />
                        </Tooltip>
                    ) : (
                        <Tooltip title="Вернуть в работу">
                            <Button type="text" icon={<UndoOutlined />} onClick={() => setStatus(task, 'pending')} />
                        </Tooltip>
                    )}
                    <Tooltip title="Изменить">
                        <Button type="text" className="task-icon-btn" icon={<EditIcon />} onClick={() => openEdit(task)} />
                    </Tooltip>
                    <Popconfirm title="Удалить задачу?" okText="Удалить" cancelText="Отмена" onConfirm={() => remove(task)}>
                        <Tooltip title="Удалить">
                            <Button type="text" danger icon={<DeleteOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ]

    const resetClosedFilters = () => {
        setRange(null)
        setBasis('closed')
        setPriorityFilter(undefined)
        setStatusFilter(undefined)
        setSearch('')
    }

    return (
        <div className="lead-workspace" style={{ paddingTop: 22 }}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Space style={{ width: '100%', justifyContent: 'space-between' }} align="center" wrap>
                    <div>
                        <Title level={3} style={{ margin: 0 }}>Задачи</Title>
                        <Text type="secondary">
                            {isManager
                                ? 'Ваши задачи и задачи сотрудников младше по роли, включая автозадачи SLA'
                                : 'Ваши задачи, включая автозадачи по правилам SLA'}
                            {tab === 'open' && tasks.length > 0 && ` · активных: ${tasks.length}`}
                            {tab === 'open' && dueCounts.overdue > 0 && `, просрочено: ${dueCounts.overdue}`}
                        </Text>
                    </div>
                    <Space wrap>
                        <Segmented
                            value={tab}
                            options={[
                                { value: 'open', label: 'Активные' },
                                { value: 'closed', label: 'Закрытые задачи' },
                            ]}
                            onChange={(value) => {
                                const next = value as Tab
                                setTab(next)
                                setDue('all')
                                setRange(null)
                                setSearch('')
                                setBasis(next === 'closed' ? 'closed' : 'deadline')
                            }}
                        />
                        {isManager && (
                            <Select
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                placeholder="Все исполнители"
                                style={{ width: 220 }}
                                value={assignee}
                                onChange={(value) => setAssignee(value)}
                                options={assignableOperators.map((operator) => ({
                                    value: operator.id,
                                    label: operator.full_name || operator.username,
                                }))}
                            />
                        )}
                        <Checkbox checked={teamOnly} onChange={(event) => setTeamOnly(event.target.checked)}>
                            Задачи моей команды
                        </Checkbox>
                        <Button icon={<ReloadOutlined />} onClick={load} loading={loading} />
                        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Новая задача</Button>
                    </Space>
                </Space>

                {tab === 'open' ? (
                    <Space wrap size={12}>
                        {/* Разбивка по сроку: сегодня, завтра и отдельно просроченные. */}
                        <Segmented
                            value={due}
                            onChange={(value) => setDue(value as Due)}
                            options={[
                                { value: 'all', label: `Все (${searchedTasks.length})` },
                                { value: 'overdue', label: `Просроченные (${dueCounts.overdue})` },
                                { value: 'today', label: `На сегодня (${dueCounts.today})` },
                                { value: 'tomorrow', label: `На завтра (${dueCounts.tomorrow})` },
                            ]}
                        />
                        {/* Произвольный период: по сроку задачи или по времени,
                            когда её поставили. */}
                        <Select
                            style={{ width: 200 }}
                            value={basis}
                            onChange={(value) => setBasis(value)}
                            options={[
                                { value: 'deadline', label: BASIS_LABELS.deadline },
                                { value: 'created', label: BASIS_LABELS.created },
                            ]}
                        />
                        <RangePicker
                            value={range}
                            onChange={(value) => setRange(value && value[0] && value[1] ? [value[0], value[1]] : null)}
                            format="DD.MM.YYYY"
                            placeholder={basis === 'created' ? ['Поставлена с', 'по'] : ['Срок с', 'по']}
                            allowClear
                        />
                        <Input.Search
                            allowClear
                            placeholder="Поиск по задаче, лиду, ID CRM"
                            style={{ width: 260 }}
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                        />
                        {(range || due !== 'all' || search) && (
                            <Button type="text" onClick={() => { setRange(null); setDue('all'); setSearch('') }}>
                                Сбросить
                            </Button>
                        )}
                    </Space>
                ) : (
                    <Space wrap size={12}>
                        <Select
                            style={{ width: 200 }}
                            value={basis}
                            onChange={(value) => setBasis(value)}
                            options={(['closed', 'deadline', 'created'] as RangeBasis[])
                                .map((value) => ({ value, label: BASIS_LABELS[value] }))}
                        />
                        <RangePicker
                            value={range}
                            onChange={(value) => setRange(value && value[0] && value[1] ? [value[0], value[1]] : null)}
                            format="DD.MM.YYYY"
                            placeholder={basis === 'closed'
                                ? ['Закрыта с', 'по']
                                : basis === 'created' ? ['Поставлена с', 'по'] : ['Срок с', 'по']}
                            allowClear
                        />
                        <Select
                            allowClear
                            placeholder="Как закрыта"
                            style={{ width: 170 }}
                            value={statusFilter}
                            onChange={setStatusFilter}
                            options={[
                                { value: 'completed', label: 'Выполнена' },
                                { value: 'cancelled', label: 'Отменена' },
                            ]}
                        />
                        <Select
                            allowClear
                            placeholder="Приоритет"
                            style={{ width: 150 }}
                            value={priorityFilter}
                            onChange={setPriorityFilter}
                            options={Object.entries(PRIORITY_LABELS).map(([value, item]) => ({ value, label: item.label }))}
                        />
                        <Input.Search
                            allowClear
                            placeholder="Поиск по задаче, лиду, ID CRM"
                            style={{ width: 260 }}
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                        />
                        <Button type="text" onClick={resetClosedFilters}>Сбросить</Button>
                    </Space>
                )}

                <Spin spinning={loading}>
                    <Table
                        rowKey="id"
                        columns={columns}
                        dataSource={visibleTasks}
                        pagination={{ pageSize: 20, hideOnSinglePage: true }}
                        scroll={{ x: 1100 }}
                        locale={{
                            emptyText: <Empty description={tab === 'closed' ? 'Закрытых задач нет' : 'Задач нет'} />,
                        }}
                    />
                </Spin>
            </Space>

            {modals}
        </div>
    )
}
